import { ReactNode } from 'react';
import { useAppLoading, type LoadingStep } from '@/src/contexts';
import { Spinner } from './ui/spinner';
import { Check } from 'lucide-react';
import { cn } from '@/src/helpers/utils';

interface AppLoadingScreenProps {
  children: ReactNode;
}

interface LoadingStepItemProps {
  step: LoadingStep;
  label: string;
  isComplete: boolean;
  isCurrent: boolean;
}

function LoadingStepItem({ label, isComplete, isCurrent }: LoadingStepItemProps) {
  return (
    <div className={cn(
      'flex items-center gap-3 transition-all duration-300',
      isComplete && 'text-primary',
      isCurrent && 'text-foreground',
      !isComplete && !isCurrent && 'text-muted-foreground'
    )}>
      <div className="flex-shrink-0 w-5 h-5">
        {isComplete ? (
          <Check className="w-5 h-5 text-primary" />
        ) : isCurrent ? (
          <Spinner variant="circle" size={20} />
        ) : (
          <div className="w-5 h-5 rounded-full border-2 border-muted-foreground/30" />
        )}
      </div>
      <span className={cn(
        'text-sm font-medium',
        isComplete && 'font-semibold'
      )}>
        {label}
      </span>
    </div>
  );
}

export function AppLoadingScreen({ children }: AppLoadingScreenProps) {
  const { isAppReady, currentStep, completedSteps } = useAppLoading();

  if (!isAppReady) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-background">
        <div className="w-full max-w-md px-4">
          <div className="text-center space-y-8">
            {/* Main Spinner */}
            <div className="flex justify-center">
              <Spinner variant="circle" size={64} />
            </div>

            {/* Title */}
            <div className="space-y-2">
              <h2 className="text-2xl font-bold">Loading Acacia DEX</h2>
              <p className="text-sm text-muted-foreground">
                Please wait while we initialize the application
              </p>
            </div>

            {/* Loading Steps */}
            <div className="space-y-4">
              <LoadingStepItem
                step="auth"
                label="Checking authentication"
                isComplete={completedSteps.includes('auth')}
                isCurrent={currentStep === 'auth'}
              />
              <LoadingStepItem
                step="xrpl"
                label="Connecting to XRPL network"
                isComplete={completedSteps.includes('xrpl')}
                isCurrent={currentStep === 'xrpl'}
              />
              <LoadingStepItem
                step="orderbook"
                label="Loading orderbook data"
                isComplete={completedSteps.includes('orderbook')}
                isCurrent={currentStep === 'orderbook'}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
